import moment from 'moment'
import React, { memo, useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslate } from 'react-redux-multilingual'
import { useLocation } from 'react-router-dom'

import Alert from '../../../components/Alert/Alert'
import FilterModal from '../../../components/FilterModal/FilterModal'
import FeedbackContent from '../../../components/PageContent/Feedback/FeedbackContent'
import {
  applyFilterData,
  extractFilterOptions,
  extractParamsFromPath,
  updateFilterData,
  updateFilterOptions,
  updateObject
} from '../../../helpers/utils'
import {
  fetchAppraisalYearOptions,
  fetchEmployeeOptions,
  fetchEmpPositions,
  fetchFormOptions,
  fetchSelfPosition
} from '../../../store/actions'
import {
  downloadFeedbackAttachment,
  fetchFeedbackEmp,
  fetchFeedbackHistory,
  fetchFeedbackStructure,
  postFeedback
} from '../../../store/actions/feedback'

const optionsMapper = {
  form_id: {
    valueKey: 'form_id',
    labelKey: 'form_name'
  },
  appraisal_year: {
    valueKey: 'year_id',
    labelKey: 'year_name'
  },
  position_id: {
    valueKey: 'position_id',
    labelKey: { name: 'position_name', code: 'position_code' }
  },
  emp_id: {
    valueKey: 'emp_id',
    labelKey: { name: 'full_name', code: 'emp_code' }
  }
}

function Feedback() {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const location = useLocation()

  const isAdmin = useSelector(state => state.auth.isAdmin)
  const formOptions = useSelector(state => state.feedback.forms)
  const yearsOptions = useSelector(state => state.feedback.years)
  const positionOptions = useSelector(state => state.feedback.positions)
  const empOptions = useSelector(state => state.feedback.emps)

  const loadingStates = useSelector(state => state.feedback.loadingStates)

  const currentFilter = useSelector(state => state.feedback.currentFilter)
  const currentUser = useSelector(state => state.feedback.currentUser)
  // const currentHistoryId = useSelector(state => state.feedback.currentHistoryId)

  const empList = useSelector(state => state.feedback.feedbackEmps)
  const structureList = useSelector(state => state.feedback.feedbackStructures)
  const chatHistory = useSelector(state => state.feedback.feedbackHistory)
  const [selectUser, setSelectUser] = useState(null)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filterControls, setFilterControls] = useState({
    appraisal_year: {
      label: translate('appraisalYear'),
      value: moment().year(),
      configs: { options: [] },
      required: true
    },
    form_id: {
      label: translate('form'),
      value: '',
      configs: { options: [] },
      required: true
    },
    emp_id: {
      label: translate('employee'),
      value: '',
      configs: { options: [] },
      required: isAdmin,
      hidden: !isAdmin
    },
    position_id: {
      label: translate('position'),
      value: '',
      configs: { options: [] },
      required: true
    }
  })

  const updateFilterItemOptions = useCallback((formKey, newOptions) => {
    setFilterControls(controls => {
      const filterItem = controls[formKey]
      const mapper = optionsMapper[formKey]

      let updatedFilter = updateFilterOptions(
        newOptions,
        filterItem,
        mapper.valueKey,
        mapper.valueKey,
        mapper.labelKey
      )

      if (formKey !== 'appraisal_year') {
        if (formKey !== 'emp_id') {
          updatedFilter = { ...updatedFilter, value: updatedFilter.configs.options[0] ?? null }
        }
      } else {
        updatedFilter = {
          ...updatedFilter,
          value: (updatedFilter.configs?.options ?? []).find(
            item => item.value === moment().year() ?? null
          )
        }
      }

      return updateObject(controls, { [formKey]: updatedFilter })
    })
  }, [])

  const filterClickedHandler = useCallback(() => {
    setFilterModalOpen(true)
  }, [])

  const filterChangedHandler = useCallback((filterKey, event) => {
    setFilterControls(controls => {
      const filterItem = controls[filterKey]
      const updatedFilter = updateFilterData(filterItem, event)
      return updateObject(controls, { [filterKey]: updatedFilter })
    })
  }, [])

  const filterCancelledHandler = useCallback(() => setFilterModalOpen(false), [])

  const filterSavedHandler = useCallback(() => {
    const data = extractFilterOptions(filterControls)
    dispatch(fetchFeedbackEmp(data))
    filterCancelledHandler()
  }, [dispatch, filterControls, filterCancelledHandler])

  const userClickedHandler = useCallback(
    selectedUser => {
      setSelectUser(selectedUser)
      dispatch(fetchFeedbackStructure(selectedUser))
    },
    [dispatch]
  )

  const itemExpandedHandler = useCallback(
    item => {
      dispatch(fetchFeedbackHistory(item.result_item_id, selectUser, 0))
    },
    [dispatch, selectUser]
  )

  const onFeedbackSubmitHandler = useCallback(
    feedbackPayload => {
      dispatch(postFeedback(feedbackPayload, selectUser))
    },
    [dispatch, selectUser]
  )

  const onFeedbackFileClickHandler = useCallback(
    feedbackHistoryId => {
      dispatch(downloadFeedbackAttachment(feedbackHistoryId))
    },
    [dispatch]
  )

  const onFeedbackScrollHandler = useCallback(
    e => {
      // const isTop = e.target.scrollTop === 0
      // if (isTop) {
      //   console.log('fetch more')
      //   dispatch(fetchFeedbackHistory(currentHistoryId, chatHistory.length))
      // }
    },
    // [dispatch, currentHistoryId, chatHistory]
    []
  )

  // Fetch parameter on mount
  useEffect(() => {
    dispatch(fetchAppraisalYearOptions())
    // dispatch(fetchFormOptions({ appraisal_year: moment().year() }))
    if (isAdmin) {
      dispatch(fetchEmployeeOptions())
      // dispatch(fetchPositionOptions())
    } else {
      dispatch(fetchSelfPosition())
    }
    return () => {}
  }, [dispatch, isAdmin])

  // Update filter options
  useEffect(() => {
    updateFilterItemOptions('form_id', formOptions)
  }, [formOptions, updateFilterItemOptions])

  useEffect(() => {
    updateFilterItemOptions('appraisal_year', yearsOptions)
  }, [yearsOptions, updateFilterItemOptions])

  useEffect(() => {
    updateFilterItemOptions('position_id', positionOptions)
  }, [positionOptions, updateFilterItemOptions])

  useEffect(() => {
    updateFilterItemOptions('emp_id', empOptions)
  }, [empOptions, updateFilterItemOptions])

  // Filter Controls sync
  useEffect(() => {
    if (currentFilter) {
      setFilterControls(controls => {
        return applyFilterData(controls, currentFilter)
      })
    }
  }, [currentFilter])

  // Cascaded filter options
  useEffect(() => {
    const filterOptions = {
      appraisal_year: filterControls.appraisal_year.value?.value ?? null
    }
    dispatch(fetchFormOptions(filterOptions))
  }, [dispatch, filterControls.appraisal_year.value])

  useEffect(() => {
    const emp_id = filterControls.emp_id.value?.value ?? null
    if (emp_id) {
      const filterOptions = {
        emp_id
      }
      dispatch(fetchEmpPositions(filterOptions))
    }
  }, [dispatch, filterControls.emp_id.value])

  useEffect(() => {
    const paramObject = extractParamsFromPath(location.search)

    if (paramObject.appraisal_year && paramObject.form_id && paramObject.position_id) {
      dispatch(fetchFeedbackEmp(paramObject))
    }
    if (paramObject.result_id) {
      dispatch(fetchFeedbackStructure({ result_id: paramObject.result_id }))
    }
  }, [dispatch, location.search])

  return (
    <>
      <Alert />
      <FilterModal
        open={filterModalOpen}
        filterControls={filterControls}
        filterChanged={filterChangedHandler}
        filterSaved={filterSavedHandler}
        cancelled={filterCancelledHandler}
        loadingQueue={loadingStates}
      />
      <FeedbackContent
        pageTranslateKey={'coachingAndFeedback'}
        filtered={!!currentFilter}
        selectedEmpId={parseInt(currentUser?.emp_detail_id) ?? null}
        empList={empList}
        structureList={structureList}
        filterClicked={filterClickedHandler}
        userClicked={userClickedHandler}
        itemExpanded={itemExpandedHandler}
        chatHistory={chatHistory}
        onFeedbackSubmit={onFeedbackSubmitHandler}
        onFeedbackFileClick={onFeedbackFileClickHandler}
        onFeedbackScroll={onFeedbackScrollHandler}
        loadingStates={loadingStates}
        isAdmin={isAdmin}
      />
    </>
  )
}

export default memo(Feedback)
